import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  alpha
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CheckCircle,
  PendingActions,
  Warning,
  MenuBook
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const StudentFeePayments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [selectedSubject, setSelectedSubject] = useState('');
  const [openSubjectDialog, setOpenSubjectDialog] = useState(false);
  const [studentSubjects, setStudentSubjects] = useState([]);

  useEffect(() => {
    fetchMyPayments();
  }, []);
  
  useEffect(() => {
    // Show subject selection dialog when student has subjects and hasn't selected yet
    if (studentSubjects.length > 0 && !selectedSubject) {
      setOpenSubjectDialog(true);
    }
  }, [studentSubjects, selectedSubject]);

  const fetchMyPayments = async (subject = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (subject) {
        params.subject = subject;
      }
      
      const response = await axios.get(`${API_URL}/fees/my-payments`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPayments(response.data.payments || []);
      setOutstanding(response.data.outstanding || []);
      setTotalOutstanding(response.data.total_outstanding || 0);
      
      // Get student subjects (only on first load)
      if (response.data.student?.subjects && studentSubjects.length === 0) {
        const subjects = typeof response.data.student.subjects === 'string'
          ? JSON.parse(response.data.student.subjects)
          : response.data.student.subjects;
        setStudentSubjects(Array.isArray(subjects) ? subjects : []);
      }
    } catch (error) {
      setAlert({ show: true, message: 'Error loading payment data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setOpenSubjectDialog(false);
    fetchMyPayments(subject);
  };

  const handleChangeSubject = () => {
    setSelectedSubject('');
    setOpenSubjectDialog(true);
  };

  const getPaidCount = () => {
    return payments.filter(p => p.payment_status === 'paid').length;
  };

  const getTotalPaid = () => {
    return payments
      .filter(p => p.payment_status === 'paid')
      .reduce((sum, p) => sum + parseFloat(p.amount), 0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
        }}
      >
        <CircularProgress sx={{ color: 'white', mb: 2 }} />
        {selectedSubject && (
          <Typography sx={{ color: 'white' }}>
            Loading {selectedSubject} fee payments...
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        py: 4
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 700,
              mb: 1
            }}
          >
            My Fee Payments
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            View your class fee payment history and outstanding dues
          </Typography>
        </Box>

        {/* Subject Filter Banner */}
        {selectedSubject && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              background: alpha('#10b981', 0.95),
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MenuBook sx={{ color: 'white', mr: 2, fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Filtered by: {selectedSubject}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Showing fee payments for {selectedSubject} classes only
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleChangeSubject}
              sx={{
                bgcolor: 'white',
                color: '#10b981',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              Change Subject
            </Button>
          </Paper>
        )}

        {/* Alert */}
        {alert.show && (
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, show: false })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PaymentIcon sx={{ fontSize: 40, color: '#f093fb', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f093fb' }}>
                      {payments.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Weeks
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ fontSize: 40, color: '#10b981', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                      {getPaidCount()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Weeks Paid
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PendingActions sx={{ fontSize: 40, color: '#f59e0b', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                      {outstanding.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Outstanding
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Warning sx={{ fontSize: 40, color: '#ef4444', mr: 2 }} />
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>
                      Rs. {totalOutstanding.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Due
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Outstanding Payments */}
        {outstanding.length > 0 && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#ef4444' }}>
              ⚠️ Outstanding Payments ({outstanding.length})
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fef2f2' }}>
                    <TableCell><strong>Week</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Class</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outstanding.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        {new Date(payment.week_start_date).toLocaleDateString()} -{' '}
                        {new Date(payment.week_end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>Rs. {parseFloat(payment.amount).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_status}
                          color={payment.payment_status === 'overdue' ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{payment.class_room?.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* All Payment History */}
        <Paper
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Complete Payment History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Paid: Rs. {getTotalPaid().toFixed(2)}
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell><strong>Week</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Paid Date</strong></TableCell>
                  <TableCell><strong>Payment Method</strong></TableCell>
                  <TableCell><strong>Class</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No payment records found
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        {new Date(payment.week_start_date).toLocaleDateString()} -{' '}
                        {new Date(payment.week_end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>Rs. {parseFloat(payment.amount).toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_status}
                          color={
                            payment.payment_status === 'paid'
                              ? 'success'
                              : payment.payment_status === 'overdue'
                              ? 'error'
                              : 'warning'
                          }
                          size="small"
                          icon={payment.payment_status === 'paid' ? <CheckCircle /> : undefined}
                        />
                      </TableCell>
                      <TableCell>
                        {payment.paid_date
                          ? new Date(payment.paid_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>{payment.payment_method || '-'}</TableCell>
                      <TableCell>{payment.class_room?.name}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Subject Selection Dialog */}
      <Dialog
        open={openSubjectDialog}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }
        }}
      >
        <DialogTitle
          sx={{
            color: 'white',
            textAlign: 'center',
            pt: 4,
            pb: 2
          }}
        >
          <MenuBook sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Select Your Subject
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Choose a subject to view fee payments
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 4, px: 4 }}>
          <Grid container spacing={2}>
            {studentSubjects.map((subject) => (
              <Grid item xs={12} key={subject}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleSubjectSelect(subject)}
                  sx={{
                    py: 2,
                    bgcolor: 'white',
                    color: '#667eea',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {subject}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default StudentFeePayments;
